import React, { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  LayoutDashboard, Clock, Users, Heart, CreditCard,
  FileText, Settings, UserCog, LogOut, Briefcase, Upload,
  Download, Mail
} from 'lucide-react';
import toast from 'react-hot-toast';
import { exportBackup, getJrsStatus, initiateJrs } from '../api/client';
import Modal from './Modal';

const NAV = [
  { section: 'Allgemein', items: [
    { to:'/dashboard', icon:LayoutDashboard, label:'Dashboard', roles:['admin','director','teacher','accountant','office'] },
  ]},
  { section: 'Schüler', items: [
    { to:'/waiting',  icon:Clock,  label:'Warteliste',      roles:['admin','director','teacher','office','parent'] },
    { to:'/students', icon:Users,  label:'Aktive Schüler',  roles:['admin','director','teacher','office','parent'] },
    { to:'/families', icon:Heart,  label:'Familien',        roles:['admin','director','office','parent'] },
  ]},
  { section: 'Finanzen', items: [
    { to:'/payments', icon:CreditCard, label:'Zahlungen', roles:['admin','director','accountant','parent'] },
    { to:'/but',      icon:Briefcase,  label:'BuT',       roles:['admin','director','accountant'] },
  ]},
  { section: 'Berichte', items: [
    { to:'/reports', icon:FileText, label:'Berichte', roles:['admin','director','accountant'] },
  ]},
  { section: 'System', items: [
    { to:'/customizing', icon:Settings, label:'Anpassung',        roles:['admin'] },
    { to:'/users',       icon:UserCog,  label:'Benutzerverwaltung', roles:['admin'] },
    { to:'/import',      icon:Upload,   label:'Daten importieren', roles:['admin'] },
  ]},
];

function JrsModal({ onClose }) {
  const [list, setList] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await getJrsStatus();
      setList(r.data);
    } catch { toast.error('Fehler beim Laden'); }
    finally { setLoading(false); }
  };

  const handleInitiate = async () => {
    if (!window.confirm('Neues Jahresrundschreiben starten? Alle bisherigen Bestätigungen werden zurückgesetzt.')) return;
    try {
      await initiateJrs();
      toast.success('Jahresrundschreiben gestartet');
      load();
    } catch { toast.error('Fehler'); }
  };

  React.useEffect(() => { load(); }, []);

  const emails = list ? list.map(f => f.email).filter(Boolean).join('; ') : '';
  const confirmed = list ? list.filter(f => f.jrs_confirmed).length : 0;

  return (
    <Modal title="📧 Jahresrundschreiben — Daten aktualisieren" onClose={onClose}>
      <div style={{background:'var(--bg2)',borderRadius:'var(--radius)',padding:'10px 14px',fontSize:13,marginBottom:14,color:'var(--muted)'}}>
        Kopieren Sie die E-Mail-Adressen und informieren Sie die Eltern. Wenn Eltern sich einloggen, sehen sie eine Aufforderung zur Bestätigung.
      </div>
      {list && (
        <>
          <div className="fg" style={{marginBottom:14}}>
            <label style={{fontWeight:600}}>E-Mail-Adressen aller Eltern</label>
            <textarea readOnly value={emails} rows={3} style={{fontSize:12,fontFamily:'var(--font-mono)',resize:'vertical'}}/>
          </div>
          <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:8}}>
            <span style={{fontSize:11,fontWeight:600,color:'var(--muted)',textTransform:'uppercase',letterSpacing:'.06em'}}>
              Antwort-Status — {confirmed}/{list.length} bestätigt
            </span>
            <button className="btn btn-ghost btn-sm" onClick={load}>🔄 Aktualisieren</button>
          </div>
          <div style={{maxHeight:280,overflowY:'auto',border:'1px solid var(--border)',borderRadius:'var(--radius)'}}>
            <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
              <thead>
                <tr style={{background:'var(--bg2)'}}>
                  <th style={{padding:'6px 10px',textAlign:'left',borderBottom:'1px solid var(--border)'}}>Familie</th>
                  <th style={{padding:'6px 10px',textAlign:'left',borderBottom:'1px solid var(--border)'}}>Elternteil</th>
                  <th style={{padding:'6px 10px',textAlign:'left',borderBottom:'1px solid var(--border)'}}>E-Mail</th>
                  <th style={{padding:'6px 10px',textAlign:'left',borderBottom:'1px solid var(--border)'}}>Status</th>
                  <th style={{padding:'6px 10px',textAlign:'left',borderBottom:'1px solid var(--border)'}}>Datum</th>
                </tr>
              </thead>
              <tbody>
                {list.map(f => (
                  <tr key={f.id} style={{borderBottom:'1px solid var(--border)'}}>
                    <td style={{padding:'6px 10px'}}>{f.name}</td>
                    <td style={{padding:'6px 10px'}}>{[f.parent_first_name, f.parent_last_name].filter(Boolean).join(' ') || f.parent_name}</td>
                    <td style={{padding:'6px 10px',fontFamily:'var(--font-mono)',fontSize:11}}>{f.email||'—'}</td>
                    <td style={{padding:'6px 10px'}}>
                      {f.jrs_confirmed
                        ? <span className="badge badge-green">✅ Bestätigt</span>
                        : <span className="badge badge-amber">⏳ Ausstehend</span>}
                    </td>
                    <td style={{padding:'6px 10px',fontSize:11,color:'var(--muted)'}}>{f.jrs_date||'—'}</td>
                  </tr>
                ))}
                {list.length === 0 && (
                  <tr><td colSpan={5} style={{textAlign:'center',padding:20,color:'var(--muted)'}}>Keine aktiven Familien</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
      {loading && <div style={{textAlign:'center',padding:20,color:'var(--muted)'}}>Laden…</div>}
      <div style={{display:'flex',justifyContent:'space-between',marginTop:20}}>
        <button className="btn btn-ghost" onClick={handleInitiate}>🆕 Neues Rundschreiben starten</button>
        <button className="btn btn-ghost" onClick={onClose}>Schließen</button>
      </div>
    </Modal>
  );
}

export default function Sidebar({ counts = {} }) {
  const { user, logout, isAdmin, isParent } = useAuth();
  const navigate = useNavigate();
  const [showJrs, setShowJrs] = useState(false);

  const handleLogout = () => { logout(); navigate('/login'); };
  const initials = (user?.fullName || user?.username || '?').slice(0,2).toUpperCase();

  const handleBackup = async () => {
    try {
      const res = await exportBackup();
      const url = URL.createObjectURL(new Blob([res.data], {type:'application/json'}));
      const a = document.createElement('a');
      const date = new Date().toISOString().slice(0,10);
      a.href = url;
      a.download = `maktab-backup-${date}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Backup heruntergeladen');
    } catch { toast.error('Fehler beim Backup'); }
  };

  return (
    <aside className="sidebar">
      <div className="sidebar-logo">
        <h1>Maktab</h1>
        <p>Schülerverwaltung</p>
      </div>

      {NAV.map(section => {
        const visible = section.items.filter(i => i.roles.includes(user?.role));
        if (!visible.length) return null;
        return (
          <div className="nav-section" key={section.section}>
            <div className="nav-label">{section.section}</div>
            {visible.map(item => (
              <NavLink
                key={item.to} to={item.to}
                className={({isActive}) => `nav-item ${isActive?'active':''}`}
              >
                <item.icon size={16} strokeWidth={1.8}/>
                <span>{item.label}</span>
                {item.to==='/waiting' && counts.waiting>0 && (
                  <span className="badge">{counts.waiting}</span>
                )}
                {item.to==='/students' && counts.students>0 && (
                  <span className="badge">{counts.students}</span>
                )}
              </NavLink>
            ))}
          </div>
        );
      })}

      <div className="sidebar-footer">
        <div className="user-card">
          <div className="user-avatar">{initials}</div>
          <div>
            <div className="user-name">{user?.fullName || user?.username}</div>
            <div className="user-role">{user?.role}</div>
          </div>
        </div>
        <div style={{display:'flex',gap:4,marginBottom:8}}>
          {isAdmin && (
            <button
              title="Jahresrundschreiben"
              onClick={() => setShowJrs(true)}
              style={{flex:1,padding:'5px 4px',background:'rgba(255,255,255,.08)',border:'none',borderRadius:'var(--radius)',color:'rgba(245,240,232,.7)',cursor:'pointer',fontSize:14,transition:'all .14s'}}
              onMouseOver={e=>e.currentTarget.style.background='rgba(255,255,255,.15)'}
              onMouseOut={e=>e.currentTarget.style.background='rgba(255,255,255,.08)'}
            >📧</button>
          )}
          {isAdmin && (
            <button
              title="Backup herunterladen"
              onClick={handleBackup}
              style={{flex:1,padding:'5px 4px',background:'rgba(255,255,255,.08)',border:'none',borderRadius:'var(--radius)',color:'rgba(245,240,232,.7)',cursor:'pointer',fontSize:14,transition:'all .14s'}}
              onMouseOver={e=>e.currentTarget.style.background='rgba(255,255,255,.15)'}
              onMouseOut={e=>e.currentTarget.style.background='rgba(255,255,255,.08)'}
            >💾</button>
          )}
        </div>
        <button className="logout-btn" onClick={handleLogout}>
          <LogOut size={13} style={{marginRight:6,verticalAlign:'middle'}}/>
          Abmelden
        </button>
      </div>

      {showJrs && <JrsModal onClose={() => setShowJrs(false)}/>}
    </aside>
  );
}
